import axios from 'axios';

import { logoutUser } from '@/pages/Login/actions';
import { deleteAuthTokens, getAuthTokens, putAuthTokens } from '@/utils/localStorage/authentication';

import events, { eventEmitter } from '@/events';
import { getAzureB2CLoginLink, reissueAzureB2CTokens } from './azureB2C';

export const reissueAzureB2CRefreshToken = async selectedLanguage => {
  deleteAuthTokens();
  window.location.href = await getAzureB2CLoginLink({
    redirectTo: window.location.pathname,
    language: selectedLanguage,
    isTokenRefresh: true,
  });
};

/**
 * If user doesn't have RememberMe option checked upon login,
 * he will have only access_token and no refresh token, thus no need for token re-issue
 * */
export const isNoTokensOrNoRefreshToken = tokens => !tokens || !tokens.refresh_token;

// Mechanism to prevent getting multiple tokens from requests when Access Token expires
let sessionErrorThrottle;
/**
 * Shared promise to prevent race condition where multiple simultaneous 401s trigger multiple token refreshes.
 * Ensures only one refresh happens at a time by reusing the same promise.
 * @type {Promise|null}
 */
let refreshTokenPromise = null;

// Test helper to reset module state between tests
export const __resetRefreshTokenPromise = () => {
  refreshTokenPromise = null;
};

export const __resetSessionErrorThrottle = () => {
  clearTimeout(sessionErrorThrottle);
  sessionErrorThrottle = null;
};

export const emitSessionExpirationWarning = () => {
  // Emit a warning event when token refresh fails and user must re-authenticate via Azure B2C.
  // This prevents immediate logout during the Azure B2C redirect flow.
  __resetSessionErrorThrottle();

  sessionErrorThrottle = setTimeout(() => {
    eventEmitter.emit(events.inApp.auth.TOKEN_EXPIRED);
  }, 500);

  // Prevent Node/Jest worker process from being kept alive by this best-effort timer.
  if (typeof sessionErrorThrottle === 'object' && sessionErrorThrottle !== null && sessionErrorThrottle.unref) {
    sessionErrorThrottle.unref();
  }
};

async function refreshTokens(tokens, langId, store) {
  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      try {
        const { data } = await reissueAzureB2CTokens(tokens.refresh_token);
        putAuthTokens(data);
        return data;
      } catch (e) {
        reissueAzureB2CRefreshToken(langId);
        throw e;
      } finally {
        refreshTokenPromise = null;
      }
    })();
  }
  await refreshTokenPromise;
}

export const requestInterceptorHandler = async (config, store) => {
  const tokens = getAuthTokens();
  const langId = store.getState().language.selectedLanguage || 'en';
  config.headers['Accept-Language'] = langId;

  if (!tokens) return config;

  const { issuedAt, expires_in, access_token, refresh_token } = tokens;
  const issuedAtTimestamp = new Date(issuedAt).getTime();
  const expiresInMs = Number(expires_in) * 1000;
  const tokenExpiresAt = issuedAtTimestamp + expiresInMs;
  const shouldRefreshToken =
    !config.url.includes('/oauth') && Number.isFinite(tokenExpiresAt) && tokenExpiresAt <= Date.now();

  if (shouldRefreshToken) {
    if (isNoTokensOrNoRefreshToken(tokens)) {
      emitSessionExpirationWarning();
      logoutUser();
      return config;
    }
    // Refresh token logic (handles race condition)
    await refreshTokens(tokens, langId, store);
  }

  // Always fetch fresh token from storage to ensure we have the latest after refresh
  const currentTokens = getAuthTokens();
  config.headers.Authorization = `Bearer ${currentTokens?.access_token}`;
  return config;
};

// FIXME: Refactor and use requestInterceptorHandler in @/api/shopHttpClient
export const responseInterceptorHandler = async (error, store) => {
  if (!error.response) {
    console.warn('[Error Log]: Axios interceptor: Response property on error object is missing: ', error);
  }

  if (error.response?.status === 401) {
    const tokens = getAuthTokens();
    const { selectedLanguage } = store.getState().language;
    const langId = selectedLanguage || 'en';

    try {
      if (!isNoTokensOrNoRefreshToken(tokens)) {
        // Use shared helper to prevent race conditions - ensures only one refresh at a time
        await refreshTokens(tokens, langId, store);
        error.config.headers.Authorization = `Bearer ${getAuthTokens()?.access_token}`;
        return axios(error.config);
      }
      emitSessionExpirationWarning();
      logoutUser();
      return Promise.reject(error);
    } catch (e) {
      return Promise.reject(error);
    }
  }

  return Promise.reject(error);
};

export const requestInterceptor = store => {
  axios.interceptors.request.use(config => requestInterceptorHandler(config, store));
};

export const responseInterceptor = store => {
  axios.interceptors.response.use(null, error => responseInterceptorHandler(error, store));
};
